import { SESSION_KEYS } from '../constants';
import { useCallback, useEffect, useRef } from 'react';

const CHECK_INTERVAL = 60 * 1000;

function useCache() {
  const isCheckingRef = useRef(false);
  const intervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const requestServiceWorkerUpdate = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return;

    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return;
    sessionStorage.setItem(SESSION_KEYS.SW_RELOAD_PENDING, 'true');
    await registration.update();
  }, []);

  const fetchBuildId = useCallback(async (): Promise<string | null> => {
    const response = await fetch(`/cache-bust.txt?t=${Date.now()}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    });

    if (!response.ok) return null;

    return (await response.text()).trim();
  }, []);

  const checkForUpdates = useCallback(async () => {
    if (isCheckingRef.current) return;

    try {
      isCheckingRef.current = true;

      const buildId = await fetchBuildId();
      if (!buildId) return;

      const storedBuildId = localStorage.getItem('app_build_id');

      if (!storedBuildId) {
        localStorage.setItem('app_build_id', buildId);
        return;
      }

      if (storedBuildId === buildId) {
        return;
      }

      localStorage.setItem('app_build_id', buildId);

      console.log('[useCache] buildId changed, request service worker update');
      await requestServiceWorkerUpdate();
    } catch (error) {
      console.warn('Failed to check for updates:', error);
    } finally {
      isCheckingRef.current = false;
    }
  }, [fetchBuildId, requestServiceWorkerUpdate]);

  const startUpdateCheck = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }

    checkForUpdates();
    intervalIdRef.current = setInterval(checkForUpdates, CHECK_INTERVAL);

    return () => {
      if (!intervalIdRef.current) return;

      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    };
  }, [checkForUpdates]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      return () => {};
    }
    return startUpdateCheck();
  }, [startUpdateCheck]);
}

export default useCache;
